<!--
 * @Author: Nemo
 * @Description: OnButton组件 - 统一风格的基础按钮组件
 * @Date: 2025-04-17
 * @LastEditors:
 * @FilePath: /rhcn-checkout-web/src/componentsV2/base/button/index.vue
-->
<template>
  <button class="btn-primary" :class="{ disabled, 'btn-loading': loading, 'btn-plain': plain }" :style="[
    {
      backgroundColor: plain ? 'transparent' : btnBackgroundColor,
      '--hover-background': plain ? 'transparent' : getHoverColor(btnBackgroundColor || '$color-button-primary'),
      '--hover-color': plain ? getHoverColor('#182058') : '#FFFFFF'
    },
    customStyle
  ]" :disabled="disabled || loading" @click="handleClick">
    <!-- 非加载状态下显示插槽内容 -->
    <template v-if="!loading">
      <slot></slot>
    </template>
    <!-- 加载状态下显示加载提示和图标 -->
    <template v-else>
      <div style="width: 100%;">
        {{ $t('message.result.processing') }}
        <i class="el-icon-loading"></i>
      </div>
    </template>
  </button>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OnButton',
  props: {
    /**
     * 是否禁用按钮
     * 禁用状态下按钮不可点击且样式变化
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否显示加载状态
     * 加载状态下按钮显示加载文本和图标，且不可点击
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否为朴素按钮
     * plain模式下按钮背景透明，带边框
     */
    plain: {
      type: Boolean,
      default: false,
    },
    /**
     * 按钮宽度
     * 可用于设置固定宽度，默认100%撑满容器
     */
    width: {
      type: String,
      default: '100%',
    },
    /**
     * 自定义样式对象
     * 可用于覆盖默认样式，例如设置自定义宽度、背景色等
     */
    customStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters(['app']),
    /**
     * 获取按钮背景色
     * 优先使用app.brandInfo.color，如果不存在则使用$color-button-primary
     * 用于统一品牌色主题
     * @returns {String} 背景色值
     */
    btnBackgroundColor() {
      return this.app && this.app.brandInfo && this.app.brandInfo.color
        ? this.app.brandInfo.color
        : '';
    },
  },
  methods: {
    /**
     * 按钮点击事件处理
     * 仅在非禁用且非加载状态下触发click事件
     * 阻止禁用或加载状态下的点击传递
     * @param {Event} e - 点击事件对象
     */
    handleClick(e) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', e);
      }
    },
    /**
     * 计算悬停时的背景色，加深原背景色8%
     * @param {String} color - 原背景色
     * @returns {String} 加深8%后的背景色
     */
    getHoverColor(color) {
      // 对于颜色值不存在的情况，返回默认加深色
      if (!color) return '#121643';

      // 处理十六进制颜色
      if (color.startsWith('#')) {
        try {
          // 解析十六进制颜色
          const hex = color.substring(1);
          let r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
          let g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
          let b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);

          // 加深颜色 (减少8%)
          r = Math.round(r * 0.92);
          g = Math.round(g * 0.92);
          b = Math.round(b * 0.92);

          // 转回十六进制
          return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        } catch (e) {
          return '#121643';
        }
      }

      // 处理rgb/rgba颜色
      if (color.startsWith('rgb')) {
        try {
          const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
          if (matches) {
            const r = Math.round(parseInt(matches[1]) * 0.92);
            const g = Math.round(parseInt(matches[2]) * 0.92);
            const b = Math.round(parseInt(matches[3]) * 0.92);
            const a = matches[4] ? matches[4] : '1';
            return `rgba(${r}, ${g}, ${b}, ${a})`;
          }
        } catch (e) {
          return '#121643';
        }
      }

      // 对于其他格式颜色，返回默认加深色
      return '#121643';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.btn-primary {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 38px;
  background-color: $color-button-primary; // 默认背景色，会被inline样式覆盖
  color: #FFFFFF;
  font-size: 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  outline: none;
  transition: all 0.3s;

  // 悬停效果
  &:hover {
    background-color: var(--hover-background) !important;
  }

  // 激活效果
  &:active {
    opacity: 0.8;
  }

  // 禁用状态样式
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // 加载状态样式
  &.btn-loading {
    opacity: 0.8;
    cursor: wait;

    // 加载图标样式
    .el-icon-loading {
      margin-left: 5px;
      animation: rotating 2s linear infinite;
    }
  }

  // Plain模式样式 - 开发者：Nemo
  &.btn-plain {
    background-color: transparent !important;
    color: #182058;
    border: 1px solid #D0D5DD;

    // 悬停效果 - 使用getHoverColor计算颜色加深
    &:hover {
      background-color: transparent !important;
      color: var(--hover-color);
      border-color: #D0D5DD;
    }

    // 激活效果
    &:active {
      background-color: transparent !important;
      opacity: 0.8;
    }

    // 禁用状态
    &.disabled {
      opacity: 0.5;
      background-color: transparent !important;
    }
  }
}

// 旋转动画
@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
